export interface IApiFailureIdentityContext {
  url?: string;
  endpoint?: string;
  method?: string;
  status?: number | string;
  errorCode?: string | number;
  graphQLOperationName?: string;
}

interface IBuildApiFailureIdentityOptions {
  source?: string;
  api?: IApiFailureIdentityContext;
  errorCode?: string | number;
}

const UUID_PATH_SEGMENT =
  /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i;
const NUMERIC_PATH_SEGMENT = /^\d+$/;
const PUBLIC_SCHEDULER_FREE_SLOTS_PATH =
  /^(\/api\/v1\/cal_sch\/public\/pages\/)[^/]+(\/free_slots\/?)$/u;

const getEndpointPath = (endpoint: string) => {
  try {
    return new URL(endpoint, 'https://api-identity.avoma.local').pathname;
  } catch {
    return endpoint.split(/[?#]/u)[0];
  }
};

export const getNormalizedApiEndpoint = (
  endpoint?: string
): string | undefined => {
  if (!endpoint) {
    return undefined;
  }

  const path = getEndpointPath(endpoint);

  const normalizedPath = path
    .split('/')
    .map(segment =>
      UUID_PATH_SEGMENT.test(segment) || NUMERIC_PATH_SEGMENT.test(segment)
        ? ':id'
        : segment
    )
    .join('/');

  return normalizedPath.replace(
    PUBLIC_SCHEDULER_FREE_SLOTS_PATH,
    '$1:slug$2'
  );
};

export const buildApiFailureIdentity = ({
  source = 'errorNotification',
  api,
  errorCode
}: IBuildApiFailureIdentityOptions): string | null => {
  const endpoint = api?.endpoint ?? api?.url;
  const normalizedEndpoint = getNormalizedApiEndpoint(endpoint);
  const graphQLOperation = api?.graphQLOperationName;
  const hasApiMetadata = Boolean(
    api?.method ||
      normalizedEndpoint ||
      api?.status != null ||
      api?.errorCode != null ||
      errorCode != null ||
      graphQLOperation
  );

  if (!hasApiMetadata) {
    return null;
  }

  return JSON.stringify({
    source,
    method: api?.method?.toUpperCase() ?? 'UNKNOWN',
    endpoint:
      normalizedEndpoint ??
      (graphQLOperation ? `graphql:${graphQLOperation}` : 'unknown'),
    status: api?.status == null ? 'unknown' : String(api.status),
    errorCode:
      api?.errorCode == null && errorCode == null
        ? 'unknown'
        : String(api?.errorCode ?? errorCode)
  });
};
